import { LoadEntityAttributes } from "./entityAttribute.types";
import { buildEntityFieldCatalog } from "./entityFieldCatalog";
import { EntityFieldCatalogEntry } from "./entityFieldCatalog.types";

// Entity metadata doesn't change while someone is working, so it is read once per
// entity and held for the session, with concurrent consumers sharing the request.
interface CachedCatalog {
  /** Everything, logical attributes included, for search. */
  all: EntityFieldCatalogEntry[];
  /** What a form can place: no logical attributes. */
  placeable: EntityFieldCatalogEntry[];
}

const catalogsByEntityName = new Map<string, CachedCatalog>();
const requestsByEntityName = new Map<string, Promise<CachedCatalog>>();

const toCached = (all: EntityFieldCatalogEntry[]): CachedCatalog => ({
  all,
  placeable: all.filter((entry) => !entry.logical),
});

/** Only for tests - the cache is otherwise session-lifetime by design. */
export function clearEntityCatalogCache() {
  catalogsByEntityName.clear();
  requestsByEntityName.clear();
}

/** What has been loaded for an entity so far, or undefined before it has. */
export function getCachedCatalog(entityName: string, includeLogical = false) {
  const cached = catalogsByEntityName.get(entityName);

  return cached && (includeLogical ? cached.all : cached.placeable);
}

/** Holds a catalog the metadata couldn't supply, so a failed read isn't retried per render. */
export function setCachedCatalog(
  entityName: string,
  entries: EntityFieldCatalogEntry[]
) {
  catalogsByEntityName.set(entityName, toCached(entries));
}

export function loadCatalog(
  entityName: string,
  load: LoadEntityAttributes,
  includeLogical = false
) {
  const pick = (cached: CachedCatalog) =>
    includeLogical ? cached.all : cached.placeable;
  const cached = catalogsByEntityName.get(entityName);
  if (cached) {
    return Promise.resolve(pick(cached));
  }

  const inFlight = requestsByEntityName.get(entityName);
  if (inFlight) {
    return inFlight.then(pick);
  }

  const request = load(entityName)
    .then((attributes) => {
      const entries = toCached(buildEntityFieldCatalog(attributes));
      catalogsByEntityName.set(entityName, entries);

      return entries;
    })
    .finally(() => {
      requestsByEntityName.delete(entityName);
    });

  requestsByEntityName.set(entityName, request);

  return request.then(pick);
}
